import { useState, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Input } from '@/components/ui/input'

// Natural language date parser
function parseNaturalLanguageDate(input: string): Date | null {
  if (!input.trim()) return null

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const inputLower = input.toLowerCase().trim()

  // Today
  if (inputLower === 'today') {
    return today
  }

  // Tomorrow
  if (inputLower === 'tomorrow') {
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow
  }

  // Yesterday
  if (inputLower === 'yesterday') {
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    return yesterday
  }

  // Next week
  if (inputLower === 'next week') {
    const nextWeek = new Date(today)
    nextWeek.setDate(nextWeek.getDate() + 7)
    return nextWeek
  }

  // This week (next Sunday if today is not Sunday)
  if (inputLower === 'this week') {
    const thisWeek = new Date(today)
    const dayOfWeek = thisWeek.getDay()
    if (dayOfWeek !== 0) {
      // If not Sunday
      thisWeek.setDate(thisWeek.getDate() + (7 - dayOfWeek))
    }
    return thisWeek
  }

  // Next month
  if (inputLower === 'next month') {
    const nextMonth = new Date(today)
    nextMonth.setMonth(nextMonth.getMonth() + 1)
    return nextMonth
  }

  // End of month
  if (inputLower === 'end of month' || inputLower === 'eom') {
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    return endOfMonth
  }

  // End of week
  if (inputLower === 'end of week' || inputLower === 'eow') {
    const endOfWeek = new Date(today)
    const dayOfWeek = endOfWeek.getDay()
    if (dayOfWeek !== 0) {
      // If not Sunday
      endOfWeek.setDate(endOfWeek.getDate() + (7 - dayOfWeek))
    }
    return endOfWeek
  }

  // Relative days: "in X days"
  const inDaysMatch = inputLower.match(/^in\s+(\d+)\s+days?$/)
  if (inDaysMatch) {
    const days = parseInt(inDaysMatch[1])
    const futureDate = new Date(today)
    futureDate.setDate(futureDate.getDate() + days)
    return futureDate
  }

  // Relative weeks: "in X weeks"
  const inWeeksMatch = inputLower.match(/^in\s+(\d+)\s+weeks?$/)
  if (inWeeksMatch) {
    const weeks = parseInt(inWeeksMatch[1])
    const futureDate = new Date(today)
    futureDate.setDate(futureDate.getDate() + weeks * 7)
    return futureDate
  }

  // Relative months: "in X months"
  const inMonthsMatch = inputLower.match(/^in\s+(\d+)\s+months?$/)
  if (inMonthsMatch) {
    const months = parseInt(inMonthsMatch[1])
    const futureDate = new Date(today)
    futureDate.setMonth(futureDate.getMonth() + months)
    return futureDate
  }

  // Next Monday, Tuesday, etc.
  const nextDayMatch = inputLower.match(
    /^next\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)$/
  )
  if (nextDayMatch) {
    const dayName = nextDayMatch[1]
    const dayMap: {
      [key: string]: number
    } = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    }

    const targetDay = dayMap[dayName]
    const currentDay = today.getDay()
    const daysUntilTarget =
      targetDay >= currentDay ? targetDay - currentDay : 7 - currentDay + targetDay

    const nextDayDate = new Date(today)
    nextDayDate.setDate(nextDayDate.getDate() + daysUntilTarget)
    return nextDayDate
  }

  // This Monday, Tuesday, etc. (current week)
  const thisDayMatch = inputLower.match(
    /^this\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)$/
  )
  if (thisDayMatch) {
    const dayName = thisDayMatch[1]
    const dayMap: {
      [key: string]: number
    } = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    }

    const targetDay = dayMap[dayName]
    const currentDay = today.getDay()
    let daysUntilTarget = targetDay - currentDay

    // If the day has already passed this week, go to next week
    if (daysUntilTarget <= 0) {
      daysUntilTarget += 7
    }

    const thisDayDate = new Date(today)
    thisDayDate.setDate(thisDayDate.getDate() + daysUntilTarget)
    return thisDayDate
  }

  // Try to parse as a regular date (fallback)
  const parsedDate = new Date(input)
  if (!isNaN(parsedDate.getTime()) && parsedDate >= new Date()) {
    return parsedDate
  }

  return null
}

export interface EnhancedDatePickerProps {
  value?: Date
  onChange: (date?: Date) => void
  placeholder?: string
  minDate?: Date
  maxDate?: Date
  disabled?: boolean
  className?: string
  error?: string
  mode?: 'create' | 'edit'
}

export function EnhancedDatePicker({
  value,
  onChange,
  placeholder = "Click to select or type 'tomorrow', 'next week', etc.",
  minDate,
  maxDate,
  disabled = false,
  className = '',
  error,
  mode = 'create',
}: EnhancedDatePickerProps) {
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(value)
  const [naturalLanguageError, setNaturalLanguageError] = useState<string | null>(null)

  // Sync with external value changes
  useEffect(() => {
    setSelectedDate(value)
  }, [value])

  // Handle external errors
  useEffect(() => {
    if (error) {
      setNaturalLanguageError(null) // Clear natural language errors when external error is present
    }
  }, [error])

  // Format date for display
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const handleNaturalLanguageDate = (input: string) => {
    if (!input.trim()) {
      setNaturalLanguageError(null)
      return null
    }

    const parsedDate = parseNaturalLanguageDate(input)

    if (parsedDate) {
      setNaturalLanguageError(null)
      return parsedDate
    } else {
      setNaturalLanguageError(
        'Unable to parse date. Please use formats like "tomorrow", "next week", "in 3 days", etc.'
      )
      return null
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    setIsTyping(true)

    // Clear any previous error when user starts typing
    setNaturalLanguageError(null)
  }

  const handleInputBlur = () => {
    setIsTyping(false)

    // If user was typing and we have a value, try to parse it
    if (inputValue.trim()) {
      const parsedDate = handleNaturalLanguageDate(inputValue)

      if (parsedDate) {
        setSelectedDate(parsedDate)
        onChange(parsedDate)
        setInputValue('') // Clear the natural language input
      }
      // If parsing failed, the error is already set by handleNaturalLanguageDate
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleInputBlur()
    }
  }

  const handleCalendarSelect = (date: Date | null) => {
    setSelectedDate(date || undefined)
    onChange(date || undefined)
    setInputValue('')
    setIsTyping(false)
    setNaturalLanguageError(null)
  }

  // Determine what to show in the input
  const getInputValue = () => {
    if (isTyping && inputValue) {
      return inputValue
    }

    if (selectedDate) {
      return formatDate(selectedDate)
    }

    return ''
  }

  const getPlaceholder = () => {
    if (error) {
      return error
    }
    if (naturalLanguageError) {
      return 'Invalid date format'
    }
    return placeholder
  }

  const defaultMinDate = minDate || new Date()

  return (
    <div className="relative">
      <DatePicker
        selected={selectedDate}
        onChange={handleCalendarSelect}
        minDate={defaultMinDate}
        maxDate={maxDate}
        disabled={disabled}
        dateFormat="MMMM d, yyyy"
        placeholderText={getPlaceholder()}
        isClearable={mode === 'edit'}
        className="w-full"
        wrapperClassName="w-full"
        customInput={
          <div className="relative w-full">
            <Input
              value={getInputValue()}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsTyping(true)}
              placeholder={getPlaceholder()}
              disabled={disabled}
              className={`w-full ${error || naturalLanguageError ? 'border-destructive' : ''} ${className}`}
            />
          </div>
        }
      />
    </div>
  )
}
